import type { FC, ReactNode } from 'react'

import { useEffect, useState } from 'react'
import { Heart } from 'lucide-react'

import { cn } from '@/lib/utils'
import { readSavedListings, toggleSavedListing } from '@/lib/marketplace/saved-listings'

interface SaveListingButtonProps {
    listingId: string
    title: string
    className?: string
}

/**
 * The bookmark control that sits over the bottom-right of a card image.
 *
 * Which listings this browser saved lives in local storage, so the state
 * starts from a static server-safe default and storage is read after mount.
 * Never read storage in the initial state: that throws during server
 * rendering. The toggle also records the favourite against the listing so the
 * public count on a seller profile is real.
 */
export const SaveListingButton: FC<SaveListingButtonProps> = ({ listingId, title, className }): ReactNode => {
    const [saved, setSaved] = useState(false)

    useEffect(() => {
        setSaved(readSavedListings().includes(listingId))
    }, [listingId])

    const toggle = (): void => {
        setSaved(toggleSavedListing(listingId))
    }

    return (
        <button
            type='button'
            onClick={toggle}
            aria-pressed={saved}
            aria-label={saved ? `Remove ${title} from saved listings` : `Save ${title}`}
            className={cn(
                'mta-focus absolute bottom-3 right-3 z-10 inline-flex size-10 items-center justify-center rounded-[10px]',
                'bg-white text-ink-900 shadow-[0_2px_8px_rgba(10,10,10,0.18)]',
                'transition-colors duration-200 hover:bg-brand-red-ink hover:text-white',
                className
            )}
        >
            <Heart className={cn('size-[22px]', saved && 'fill-current')} strokeWidth={2.2} aria-hidden='true' />
        </button>
    )
}
